import {
  createEmptyLetterTemplateDocument,
  letterTemplateDocumentsEqual,
} from './letterTemplateDocument.js';

/**
 * @typedef {{
 *   bilingual: boolean,
 *   documents: { fr: object, en?: object },
 *   translation?: {
 *     sourceLanguage: string,
 *     targetLanguage: string,
 *     sourceDocumentHash: string,
 *     model: string,
 *   } | null,
 * }} LetterTemplateRichDraft
 */

/**
 * @returns {LetterTemplateRichDraft}
 */
export function createDefaultMonolingualRichDraft() {
  return {
    bilingual: false,
    documents: { fr: createEmptyLetterTemplateDocument() },
    translation: null,
  };
}

/**
 * @param {LetterTemplateRichDraft} draft
 * @returns {boolean}
 */
export function letterTemplateRichDraftsEqual(a, b) {
  if (!a || !b) return false;
  if (Boolean(a.bilingual) !== Boolean(b.bilingual)) return false;
  if (!letterTemplateDocumentsEqual(a.documents?.fr, b.documents?.fr)) return false;
  if (a.bilingual) {
    if (!letterTemplateDocumentsEqual(a.documents?.en, b.documents?.en)) return false;
  }
  return true;
}

/**
 * @param {object | null | undefined} document
 */
export function letterTemplateDocumentHasMeaningfulContent(document) {
  if (!document || document.type !== 'doc') return false;
  const content = Array.isArray(document.content) ? document.content : [];
  if (content.length === 0) return false;
  if (content.length > 1) return true;
  const only = content[0];
  if (!only) return false;
  if (only.type === 'letterImage' || only.type === 'letterQrBlock') return true;
  if (only.type === 'paragraph' || only.type === 'heading') {
    const inline = Array.isArray(only.content) ? only.content : [];
    if (inline.length === 0) return false;
    return inline.some((node) => {
      if (node.type === 'text') return (node.text || '').trim().length > 0;
      if (node.type === 'letterPlaceholder') return true;
      return false;
    });
  }
  return true;
}

/**
 * @param {boolean} bilingual
 * @param {object} frenchDocument
 * @param {object | null | undefined} englishDocument
 * @param {object | null | undefined} translation
 */
export function buildLetterTemplateRichDraft({ bilingual, frenchDocument, englishDocument, translation }) {
  const draft = {
    bilingual: Boolean(bilingual),
    documents: { fr: frenchDocument },
    translation: translation || null,
  };
  if (draft.bilingual) {
    draft.documents.en = englishDocument || createEmptyLetterTemplateDocument();
  }
  return draft;
}

/**
 * @param {LetterTemplateRichDraft} draft
 */
export function enableBilingualOnDraft(draft) {
  return {
    ...draft,
    bilingual: true,
    documents: {
      ...draft.documents,
      en: draft.documents?.en || createEmptyLetterTemplateDocument(),
    },
  };
}

/**
 * @param {LetterTemplateRichDraft} draft
 */
export function disableBilingualOnDraft(draft) {
  return {
    bilingual: false,
    documents: { fr: draft.documents?.fr || createEmptyLetterTemplateDocument() },
    translation: null,
  };
}

/**
 * @param {object | null | undefined} englishDocument
 */
export function shouldConfirmDisableBilingual(englishDocument) {
  return letterTemplateDocumentHasMeaningfulContent(englishDocument);
}

/**
 * @param {object | null | undefined} englishDocument
 */
export function shouldConfirmRetranslate(englishDocument) {
  return letterTemplateDocumentHasMeaningfulContent(englishDocument);
}
